// components/MaxiPitchBenefits.jsx
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TbTargetArrow } from "react-icons/tb";
import imgone from "../../assets/images/handshake.jpeg";
import Footer from "../layout/Footer";
import Header from "../layout/Header";
import {
  ChartColumnDecreasing,
  Rocket,
  ShieldCheck,
  Workflow,
  Zap,
} from "lucide-react";
import { GrIntegration } from "react-icons/gr";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { motion } from "framer-motion";
import AuthModal from "../auth/AuthModal";
import { useNavigate } from "react-router-dom";
import Meta from "../Meta/Meta";
const MaxiPitchBenefits = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const leadGrowthData = [
    { month: "Jan", leads: 45 },
    { month: "Feb", leads: 52 },
    { month: "Mar", leads: 48 },
    { month: "Apr", leads: 78 },
    { month: "May", leads: 82 },
    { month: "Jun", leads: 90 },
  ];

  const conversionData = [
    { name: "With MaxiPitch", value: 75, color: "#4F46E5" },
    { name: "Traditional Method ", value: 25, color: "#10B981" },
  ];

  const roiData = [
    { month: "Jan", roi: 120 },
    { month: "Feb", roi: 145 },
    { month: "Mar", roi: 180 },
    { month: "Apr", roi: 210 },
    { month: "May", roi: 240 },
    { month: "Jun", roi: 285 },
  ];

  const benefits = [
    {
      icon: <Rocket />,
      title: "AI-Powered Lead Generation",
      description:
        "Our advanced AI algorithms identify and qualify leads 5x faster than traditional methods.",
      stats: "500% faster lead qualification",
    },
    {
      icon: <TbTargetArrow />,
      title: "Precision Targeting",
      description:
        "Target exactly the right audience with our intelligent filtering system.",
      stats: "98% targeting accuracy",
    },
    {
      icon: <ChartColumnDecreasing />,
      title: "Real-Time Analytics",
      description:
        "Monitor campaign performance with live dashboards and insights.",
      stats: "Real-time performance tracking",
    },
    {
      icon: <Workflow />,
      title: "Automated Outreach",
      description:
        "Automate personalized follow-ups and communication sequences.",
      stats: "Save 15+ hours weekly",
    },
    {
      icon: <GrIntegration />,
      title: "Seamless Integration",
      description:
        "Connect with your existing CRM and marketing tools effortlessly.",
      stats: "20+ platform integrations",
    },
    {
      icon: <FaMoneyBillTrendUp />,
      title: "High ROI",
      description:
        "Achieve up to 300% return on investment with optimized lead generation.",
      stats: "300% average ROI",
    },
  ];

  const features = [
    "Smart Lead Scoring",
    "Automated Follow-ups",
    "Multi-channel Campaigns",
    "Performance Analytics",
  ];
  const isMobile = window.innerWidth < 640;
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState("signup");

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const [openWaitingListModal, setOpenWaitingListModal] = useState(false);
  return (
    <div className="min-h-screen  ">
      <Meta
        title="MaxiPitch Benefits | AI Lead Generation & Sales Automation"
        description="Explore MaxiPitch benefits including AI lead generation, automated outreach, & smart sales tools designed to help businesses grow faster with less manual work."
        url="https://maxipitch.com/maxipitch-benefits"
      />
      <div className="">
        <Header />
      </div>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="py-8 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 text-center pt-14 max-md:pt-8 max-md:px-4 mb-8"
      >
        <h1 className=" bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent mb-6 max-md:mb-2 ">
          Get More Leads Without the Hassle
        </h1>
        <p className="text-xl max-md:text-base text-gray-500 max-w-2xl mx-auto max-md:px-2">
          MaxiPitch uses cutting-edge AI to revolutionize how you attract,
          engage, and convert prospects. Experience 3x more qualified leads in
          your first month.
        </p>
      </motion.div>
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto max-md:px-4">
        {/* Stats Section */}
        <div className="grid grid-cols-1 max-md:grid-cols-2 md:grid-cols-4 gap-6 mb-14 max-md:mb-12">
          <div className=" p-6  hover:shadow-xl transition-shadow max-md:p-4  border-2 border-indigo-200 shadow-md bg-white rounded-2xl">
            <div className="text-3xl font-bold text-indigo-600">3x</div>
            <div className="text-gray-500">More Qualified Leads</div>
          </div>
          <div className=" p-6  hover:shadow-xl transition-shadow max-md:p-4  border-2 border-indigo-200 shadow-md bg-white rounded-2xl">
            <div className="text-3xl font-bold text-green-600">75%</div>
            <div className="text-gray-500">Higher Conversion Rate</div>
          </div>
          <div className=" p-6  hover:shadow-xl transition-shadow max-md:p-4  border-2 border-indigo-200 shadow-md bg-white rounded-2xl ">
            <div className="text-3xl font-bold text-purple-600">60%</div>
            <div className="text-gray-500">Cost Reduction</div>
          </div>
          <div className=" p-6 hover:shadow-xl transition-shadow max-md:p-4  border-2 border-indigo-200 shadow-md bg-white rounded-2xl">
            <div className="text-3xl font-bold text-blue-600">24/7</div>
            <div className="text-gray-500">Automated Operation</div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Lead Growth Chart */}
          <div className="rounded-2xl p-6 shadow-lg bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
            <h3 className="">Monthly Lead Growth</h3>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={leadGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="leads"
                    stroke="#4F46E5"
                    strokeWidth={3}
                    dot={{ fill: "#4F46E5", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Conversion Rate Comparison */}
          <div className="rounded-2xl p-4 sm:p-6 shadow-lg bg-gradient-to-br from-white/80 to-indigo-50 backdrop-blur overflow-x-auto">
            {/* Title */}
            <h3 className="text-lg sm:text-xl font-semibold whitespace-nowrap">
              Conversion Rate Comparison
            </h3>

            {/* Chart Container */}
            <div className="h-64 sm:h-72  min-w-[300px] max-md:text-[7px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={conversionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={window.innerWidth < 640 ? 70 : 90}
                    dataKey="value"
                  >
                    {conversionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        {/* Why Choose MaxiPitch Section - Cards Left, Image Right */}
        <div className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Benefits Cards in 2x2 Grid */}
            <div>
              <h2 className=" mb-12 max-md:mb-4">Why Choose MaxiPitch?</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-indigo-200 "
                  >
                    <div className="flex items-start mb-4">
                      <div className="text-3xl mr-3">{benefit.icon}</div>
                      <h3 className="text-xl font-bold text-gray-800">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4">{benefit.description}</p>
                    <div className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full inline-block">
                      {benefit.stats}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Vector Image with Stats */}
            <div className="relative">
              {/* Vector Image Container */}
              <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl p-8 lg:p-12 h-full min-h-[400px] flex flex-col justify-center items-center">
                {/* Abstract vector graphic */}
                <div className="relative w-full max-w-md mx-auto">
                  {/* Main circular graphic */}
                  <div className="relative w-64 h-64 mx-auto">
                    {/* Outer circle */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-200 to-purple-300 rounded-full opacity-30 animate-pulse"></div>

                    {/* Middle circle */}
                    <div
                      className="absolute inset-8 bg-gradient-to-br from-indigo-300 to-purple-400 rounded-full opacity-40 animate-pulse"
                      style={{ animationDelay: "0.5s" }}
                    ></div>

                    {/* Inner circle */}
                    <div className="absolute inset-16 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full opacity-50 flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">AI</span>
                    </div>

                    {/* Floating elements */}
                    <div className="absolute -top-2 left-12 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center">
                      <span className="text-2xl">
                        <Rocket />
                      </span>
                    </div>
                    <div className="absolute -bottom-2 right-12 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center">
                      <span className="text-2xl">
                        <ChartColumnDecreasing />
                      </span>
                    </div>
                    <div className="absolute top-12 -left-2 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center">
                      <span className="text-2xl">
                        <TbTargetArrow />
                      </span>
                    </div>
                    <div className="absolute bottom-12 -right-2 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center">
                      <span className="text-2xl">
                        <FaMoneyBillTrendUp />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stats Overlay */}
                <div className="mt-8 grid grid-cols-2 gap-4 w-full max-w-md">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg">
                    <div className="text-2xl font-bold text-indigo-700">3x</div>
                    <div className="text-sm text-gray-600">More Leads</div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg">
                    <div className="text-2xl font-bold text-green-700">75%</div>
                    <div className="text-sm text-gray-600">Faster</div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg">
                    <div className="text-2xl font-bold text-purple-700">
                      500+
                    </div>
                    <div className="text-sm text-gray-600">Businesses</div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg">
                    <div className="text-2xl font-bold text-blue-700">24/7</div>
                    <div className="text-sm text-gray-600">Support</div>
                  </div>
                </div>
              </div>

              {/* Bottom Row Cards (2 more benefits) */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.slice(4, 6).map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 border-indigo-200 "
                  >
                    <div className="flex items-start mb-3">
                      <div className="text-2xl mr-3">{benefit.icon}</div>
                      <h3 className="text-lg font-bold text-gray-800">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">
                      {benefit.description}
                    </p>
                    <div className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full inline-block">
                      {benefit.stats}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left Content */}
          <div className="bg-white rounded-2xl p-8 shadow-lg ">
            <h2 className="text-4xl max-md:text-3xl font-bold text-gray-900 mb-6">
              Proven ROI That Speaks for Itself
            </h2>
            <p className="text-gray-600 mb-6 text-lg max-md:text-base">
              See how businesses achieve remarkable returns with MaxiPitch's
              AI-driven approach to lead generation.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl">
                    <ChartColumnDecreasing />
                  </span>
                </div>
                <div>
                  <h3 className=" mb-2">Consistent Growth</h3>
                  <p className="text-gray-600">
                    Our customers experience an average ROI increase of 25%
                    month-over-month, demonstrating sustainable and scalable
                    growth.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl">
                    <Zap />
                  </span>
                </div>
                <div>
                  <h3 className=" mb-2">Quick Results</h3>
                  <p className="text-gray-600">
                    Most users see significant ROI within the first 30 days of
                    implementation, with full optimization achieved by month 3.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl">
                    <TbTargetArrow />
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Predictable Returns
                  </h3>
                  <p className="text-gray-600">
                    With our AI-powered forecasting, businesses can accurately
                    predict and plan for future growth based on historical ROI
                    data.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
              <p className="text-lg font-semibold text-gray-800 mb-2">
                "MaxiPitch delivered 285% ROI in just 6 months."
              </p>
            </div>
          </div>

          {/* Right Side - ROI Graph */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className=" mb-6">Return on Investment Tracking</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roiData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    stroke="#666"
                    tick={{ fill: "#4B5563", fontSize: 14 }}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fill: "#4B5563", fontSize: 14 }}
                    label={{
                      value: "ROI (%)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: { fill: "#4B5563", fontSize: 14 },
                    }}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}% ROI`, "Return"]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    labelStyle={{ fontWeight: "bold", color: "#374151" }}
                  />
                  <Bar
                    dataKey="roi"
                    fill="#10B981"
                    radius={[6, 6, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4 text-center">
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-xl font-bold text-green-700">285%</div>
                <div className="text-sm text-gray-600">Max ROI</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-xl font-bold text-blue-700">180%</div>
                <div className="text-sm text-gray-600">Avg ROI</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="text-xl font-bold text-purple-700">120%</div>
                <div className="text-sm text-gray-600">Min ROI</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        {/* Powerful Features Section - Data Left, Vector Image Right */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-8 max-md:py-4 max-md:px-4 mb-16 max-md:mb-8">
          <h2 className="  max-md:text-start text-black">
            Powerful Features Included
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Features List */}
            <div>
              <h3 className=" text-gray-800 mb-4 pt-2">
                Everything You Need for Success
              </h3>
              <p className="text-gray-600 mb-8 max-md:text-base text-lg">
                MaxiPitch combines cutting-edge AI technology with powerful
                tools designed to streamline your lead generation process and
                maximize conversions.
              </p>

              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center bg-white p-2 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 group"
                  >
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold">✓</span>
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 text-lg">
                        {feature}
                      </span>
                      <div className="w-full bg-gray-100 h-1 mt-2 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-1000"
                          style={{
                            width: `${Math.min(100, 70 + index * 5)}%`,
                            animationDelay: `${index * 200}ms`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-3 bg-white rounded-xl shadow-sm">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">
                      <ShieldCheck />
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">
                      All features included in every plan
                    </p>
                    <p className="text-sm text-gray-600">
                      No hidden charges or feature restrictions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Vector Image */}
            <div className="relative h-full min-h-[350px] ">
              {/* Vector Image Container */}
              <div>
                <img
                  src={imgone}
                  alt="Tech Illustration"
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </div>

              {/* Feature Tagline */}
              <div className="mt-6 text-center">
                <p className="text-lg font-semibold text-gray-800">
                  Visualize Your Success with Real-Time Insights
                </p>
                <p className="text-gray-600 mt-2">
                  Our intuitive dashboard makes complex data simple and
                  actionable
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
      </div>
      <div className="text-center bg-gradient-to-r from-indigo-50 via-white to-blue-50 py-8 text-black mb-8 w-full ">
        <h2 className=" mb-6 max-md:px-6">
          Ready to Transform{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 ml-2">
            Your Lead Generation?
          </span>
        </h2>
        <p className="text-xl mb-8 opacity-90">
          Join 5,000+ businesses already using MaxiPitch
        </p>
        <div className="flex  sm:flex-row gap-4 justify-center max-md:px-6">
          <button
            className="main-button text-white font-semibold max-md:w-1/2 px-12 py-2 max-md:px-6 max-md:py-2 rounded-md text-lg transition-colors"
            onClick={() => navigate("/")}
          >
            Try it out
          </button>
          {/* <button className="bg-transparent border-2 max-md:px-6 max-md:py-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-colors">
              Schedule a Demo
            </button> */}
        </div>
        <p className="mt-6 text-sm opacity-80">No credit card required</p>
      </div>
      <Footer />


      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultTab={authModalTab}
      />
    </div>
  );
};

export default MaxiPitchBenefits;
